import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { notificationsApi } from "@/services/api";

type NotifType = "assignment" | "status" | "deadline" | "approval" | "payment" | "deliverable" | "system";

interface Notification {
  id: number;
  type: NotifType;
  title: string;
  message: string;
  time: string;
  read: boolean;
  is_read?: boolean;
  priority: "high" | "medium" | "low";
  link?: string;
  actor?: string;
  actorAvatar?: string;
  jobNo?: string;
  category?: string;
}

const typeConfig: Record<NotifType, { icon: string; color: string; bg: string; border: string; label: string }> = {
  deadline:    { icon: "ri-alarm-warning-line",       color: "text-red-600",    bg: "bg-red-50",     border: "border-red-200",    label: "Job Deadlines" },
  payment:     { icon: "ri-money-rupee-circle-line",  color: "text-orange-600", bg: "bg-orange-50",  border: "border-orange-200", label: "Overdue Invoices" },
  approval:    { icon: "ri-checkbox-circle-line",     color: "text-violet-600", bg: "bg-violet-50",  border: "border-violet-200", label: "Pending Approvals" },
  deliverable: { icon: "ri-folder-check-line",        color: "text-teal-600",   bg: "bg-teal-50",    border: "border-teal-200",   label: "Deliverable Approvals" },
  assignment:  { icon: "ri-user-add-line",            color: "text-amber-600", bg: "bg-amber-50",   border: "border-amber-200",  label: "Assignments" },
  status:      { icon: "ri-refresh-line",             color: "text-amber-600",  bg: "bg-amber-50",   border: "border-amber-200",  label: "Status Updates" },
  system:      { icon: "ri-server-line",              color: "text-gray-500",   bg: "bg-gray-100",   border: "border-gray-200",   label: "System" },
};

const priorityConfig = {
  high:   { label: "High",   cls: "bg-red-100 text-red-600" },
  medium: { label: "Medium", cls: "bg-amber-100 text-amber-600" },
  low:    { label: "Low",    cls: "bg-gray-100 text-gray-500" },
};

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function AlertToggle({ label, defaultEnabled }: { label: string; defaultEnabled: boolean }) {
  const [enabled, setEnabled] = useState(defaultEnabled);
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <button
        onClick={() => setEnabled(!enabled)}
        className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${enabled ? "bg-amber-500" : "bg-gray-200"}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${enabled ? "left-4" : "left-0.5"}`} />
      </button>
    </div>
  );
}

// ── Categorized alert card ──────────────────────────────────────────────────
interface AlertCardProps {
  notif: Notification;
  isSelected: boolean;
  onSelect: () => void;
  onMarkRead: () => void;
  onDelete: () => void;
}

function AlertCard({ notif, isSelected, onSelect, onMarkRead, onDelete }: AlertCardProps) {
  const cfg = typeConfig[notif.type];
  return (
    <div
      onClick={onSelect}
      className={`relative flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all group ${
        isSelected
          ? "border-amber-400 bg-amber-50/40"
          : notif.read
          ? "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      {!notif.read && (
        <span className="absolute top-3.5 right-3.5 w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" />
      )}
      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${cfg.bg}`}>
        <i className={`${cfg.icon} text-base ${cfg.color}`} />
      </div>
      <div className="flex-1 min-w-0 pr-4">
        <p className={`text-xs font-semibold truncate ${notif.read ? "text-gray-600" : "text-gray-900"}`}>{notif.title}</p>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">{notif.message}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${priorityConfig[notif.priority].cls}`}>
            {priorityConfig[notif.priority].label}
          </span>
          {notif.jobNo && (
            <span className="text-xs text-amber-600 font-medium">{notif.jobNo}</span>
          )}
          <span className="text-xs text-gray-400 ml-auto">{timeAgo(notif.time)}</span>
        </div>
      </div>
      {/* Hover actions */}
      <div className="absolute top-2 right-6 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!notif.read && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
            className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-gray-200 text-amber-600 hover:bg-amber-50 cursor-pointer"
            title="Mark as read"
          >
            <i className="ri-check-line text-xs" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-gray-200 text-red-400 hover:bg-red-50 cursor-pointer"
          title="Delete"
        >
          <i className="ri-delete-bin-line text-xs" />
        </button>
      </div>
    </div>
  );
}

// ── Category section ────────────────────────────────────────────────────────
interface CategorySectionProps {
  type: NotifType;
  notifications: Notification[];
  selectedId: number | null;
  onSelect: (n: Notification) => void;
  onMarkRead: (id: number) => void;
  onDelete: (id: number) => void;
  defaultOpen?: boolean;
}

function CategorySection({ type, notifications, selectedId, onSelect, onMarkRead, onDelete, defaultOpen = true }: CategorySectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const cfg = typeConfig[type];
  const unread = notifications.filter((n) => !n.read).length;

  if (notifications.length === 0) return null;

  return (
    <div className={`rounded-xl border overflow-hidden ${cfg.border}`}>
      {/* Section header */}
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 cursor-pointer transition-all ${cfg.bg} hover:opacity-90`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-white/60`}>
            <i className={`${cfg.icon} text-sm ${cfg.color}`} />
          </div>
          <span className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</span>
          <span className="text-xs bg-white/70 text-gray-700 px-2 py-0.5 rounded-full font-semibold">
            {notifications.length}
          </span>
          {unread > 0 && (
            <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold">
              {unread} new
            </span>
          )}
        </div>
        <i className={`ri-arrow-down-s-line text-lg ${cfg.color} transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Items */}
      {open && (
        <div className="divide-y divide-gray-50 bg-white">
          {notifications.map((n) => (
            <div key={n.id} className="px-3 py-1.5">
              <AlertCard
                notif={n}
                isSelected={selectedId === n.id}
                onSelect={() => onSelect(n)}
                onMarkRead={() => onMarkRead(n.id)}
                onDelete={() => onDelete(n.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detail panel ────────────────────────────────────────────────────────────
interface DetailPanelProps {
  notif: Notification;
  onMarkRead: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function DetailPanel({ notif, onMarkRead, onDelete, onClose }: DetailPanelProps) {
  const navigate = useNavigate();
  const cfg = typeConfig[notif.type];

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col">
      {/* Header */}
      <div className={`px-5 py-4 border-b ${cfg.border} ${cfg.bg} flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/70">
            <i className={`${cfg.icon} text-xl ${cfg.color}`} />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{notif.title}</p>
            <p className={`text-xs mt-0.5 ${cfg.color} font-medium`}>{cfg.label} · {timeAgo(notif.time)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${priorityConfig[notif.priority].cls}`}>
            {priorityConfig[notif.priority].label} priority
          </span>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/60 cursor-pointer text-gray-500"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4 flex-1 overflow-y-auto">
        {/* Message */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-700 leading-relaxed">{notif.message}</p>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-3">
          {notif.actor && (
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <p className="text-xs text-gray-400 mb-2">Triggered by</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {notif.actorAvatar}
                </div>
                <span className="text-sm font-semibold text-gray-800">{notif.actor}</span>
              </div>
            </div>
          )}
          {notif.jobNo && (
            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <p className="text-xs text-gray-400 mb-2">Related Job</p>
              <p className="text-sm font-bold text-amber-600">{notif.jobNo}</p>
            </div>
          )}
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-xs text-gray-400 mb-2">Date &amp; Time</p>
            <p className="text-sm font-semibold text-gray-800">
              {new Date(notif.time).toLocaleString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit", hour12: true,
              })}
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-xs text-gray-400 mb-2">Status</p>
            <span className={`text-sm font-semibold ${notif.read ? "text-gray-500" : "text-amber-600"}`}>
              {notif.read ? "Read" : "Unread"}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {notif.link && (
            <button
              onClick={() => navigate(notif.link!)}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap text-white ${
                notif.type === "deadline" ? "bg-red-500 hover:bg-red-600" :
                notif.type === "payment" ? "bg-orange-500 hover:bg-orange-600" :
                notif.type === "approval" ? "bg-violet-600 hover:bg-violet-700" :
                notif.type === "deliverable" ? "bg-teal-600 hover:bg-teal-700" :
                "bg-amber-600 hover:bg-amber-700"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-circle-line" /></span>
              {notif.type === "deadline" ? "View Job Card" :
               notif.type === "payment" ? "View Invoice" :
               notif.type === "approval" ? "Review & Approve" :
               notif.type === "deliverable" ? "View Deliverable" :
               "View Related Page"}
            </button>
          )}
          <div className="flex gap-2">
            {!notif.read && (
              <button
                onClick={onMarkRead}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line" /></span>
                Mark Read
              </button>
            )}
            <button
              onClick={onDelete}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-delete-bin-line" /></span>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Notification | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "unread" | "categorized">("categorized");
  const [filterType, setFilterType] = useState<NotifType | "all">("all");

  useEffect(() => {
    notificationsApi.getAll()
      .then((data: unknown) => {
        const list = Array.isArray(data) ? data : (data as { notifications?: unknown[] }).notifications ?? [];
        const mapped = (list as Record<string, unknown>[]).map((n) => ({
          id: n.id as number,
          type: (n.type as NotifType) ?? "system",
          title: (n.title as string) ?? "",
          message: (n.message as string) ?? "",
          time: (n.created_at as string) ?? (n.time as string) ?? new Date().toISOString(),
          read: Boolean(n.is_read ?? n.read),
          priority: (n.priority as "high" | "medium" | "low") ?? "low",
          link: n.link as string | undefined,
          actor: n.actor as string | undefined,
          actorAvatar: n.actorAvatar as string | undefined,
          jobNo: n.job_no as string | undefined ?? n.jobNo as string | undefined,
          category: n.category as string | undefined,
        }));
        setNotifications(mapped);
      })
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false));
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = (id: number) => {
    notificationsApi.markAsRead(id).catch(() => {});
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    setSelected((prev) => prev?.id === id ? { ...prev, read: true } : prev);
  };

  const markAllRead = () => {
    notificationsApi.markAllAsRead().catch(() => {});
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const deleteNotif = (id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const handleSelect = (n: Notification) => {
    setSelected(n);
    if (!n.read) markRead(n.id);
  };

  // Categorized groups — priority order
  const categoryOrder: NotifType[] = ["deadline", "payment", "approval", "deliverable", "assignment", "status", "system"];

  const getByType = (type: NotifType) => notifications.filter((n) => n.type === type);

  // Flat filtered list for "all" / "unread" tabs
  const flatFiltered = notifications.filter((n) => {
    if (activeTab === "unread") return !n.read;
    if (filterType !== "all") return n.type === filterType;
    return true;
  });

  // KPI counts
  const urgentCount = notifications.filter((n) => !n.read && n.priority === "high").length;
  const deadlineCount = notifications.filter((n) => n.type === "deadline").length;
  const overdueInvoiceCount = notifications.filter((n) => n.type === "payment").length;
  const pendingApprovalCount = notifications.filter((n) => (n.type === "approval" || n.type === "deliverable") && !n.read).length;

  if (loading) {
    return (
      <ERPLayout title="Notifications">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-3xl text-amber-500 animate-spin" />
            <p className="text-sm text-gray-400 mt-2">Loading notifications…</p>
          </div>
        </div>
      </ERPLayout>
    );
  }

  return (
    <ERPLayout title="Notifications">
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Notification Center</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {unreadCount > 0
                ? <><span className="text-amber-600 font-semibold">{unreadCount} unread</span> · {notifications.length} total</>
                : "All caught up!"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg font-medium hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-double-line" /></span>
                Mark All Read
              </button>
            )}
          </div>
        </div>

        {/* Alert Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          {[
            {
              label: "Urgent Alerts", value: urgentCount, icon: "ri-error-warning-line",
              color: "text-red-600", bg: "bg-red-50", border: "border-red-100",
              sub: "High priority unread",
            },
            {
              label: "Job Deadlines", value: deadlineCount, icon: "ri-alarm-warning-line",
              color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100",
              sub: `${notifications.filter((n) => n.type === "deadline" && !n.read).length} overdue or due soon`,
            },
            {
              label: "Overdue Invoices", value: overdueInvoiceCount, icon: "ri-money-rupee-circle-line",
              color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100",
              sub: `${notifications.filter((n) => n.type === "payment" && !n.read).length} need follow-up`,
            },
            {
              label: "Pending Approvals", value: pendingApprovalCount, icon: "ri-checkbox-circle-line",
              color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-100",
              sub: "Quotations & deliverables",
            },
          ].map((kpi) => (
            <div key={kpi.label} className={`bg-white rounded-xl border ${kpi.border} p-4`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${kpi.bg}`}>
                  <i className={`${kpi.icon} text-xl ${kpi.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                  <p className="text-xs text-gray-500">{kpi.label}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400">{kpi.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            {([
              { key: "categorized", label: "By Category" },
              { key: "all", label: "All" },
              { key: "unread", label: `Unread (${unreadCount})` },
            ] as { key: typeof activeTab; label: string }[]).map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-amber-700" : "text-gray-500 hover:text-gray-700"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {(activeTab === "all") && (
            <div className="flex gap-1 flex-wrap">
              {(["all", ...categoryOrder] as (NotifType | "all")[]).map((t) => {
                const cfg = t === "all" ? null : typeConfig[t];
                const count = t === "all" ? notifications.length : notifications.filter((n) => n.type === t).length;
                if (count === 0 && t !== "all") return null;
                return (
                  <button
                    key={t}
                    onClick={() => setFilterType(t)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${filterType === t ? (cfg ? `${cfg.bg} ${cfg.color} border ${cfg.border}` : "bg-gray-800 text-white") : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                  >
                    {t === "all" ? `All (${count})` : `${cfg!.label} (${count})`}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="grid grid-cols-3 gap-5">
          {/* Left: notification list */}
          <div className="col-span-2 space-y-3 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
            {activeTab === "categorized" && (
              <>
                {categoryOrder.map((type) => (
                  <CategorySection
                    key={type}
                    type={type}
                    notifications={getByType(type)}
                    selectedId={selected?.id ?? null}
                    onSelect={handleSelect}
                    onMarkRead={markRead}
                    onDelete={deleteNotif}
                    defaultOpen={["deadline", "payment", "approval", "deliverable"].includes(type)}
                  />
                ))}
              </>
            )}

            {(activeTab === "all" || activeTab === "unread") && (
              <div className="space-y-1.5">
                {flatFiltered.map((n) => {
                  const cfg = typeConfig[n.type];
                  return (
                    <div
                      key={n.id}
                      onClick={() => handleSelect(n)}
                      className={`relative flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all group ${
                        selected?.id === n.id
                          ? "border-amber-400 bg-amber-50/40"
                          : n.read
                          ? "bg-white border-gray-100 hover:border-gray-200"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      {!n.read && <span className="absolute top-3.5 right-3.5 w-2 h-2 bg-amber-500 rounded-full" />}
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${cfg.bg}`}>
                        <i className={`${cfg.icon} text-base ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className={`text-xs font-semibold truncate ${n.read ? "text-gray-600" : "text-gray-900"}`}>{n.title}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-1">{n.message}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${priorityConfig[n.priority].cls}`}>{priorityConfig[n.priority].label}</span>
                          {n.jobNo && <span className="text-xs text-amber-600 font-medium">{n.jobNo}</span>}
                          <span className="text-xs text-gray-400 ml-auto">{timeAgo(n.time)}</span>
                        </div>
                      </div>
                      <div className="absolute top-2 right-6 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!n.read && (
                          <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-gray-200 text-amber-600 hover:bg-amber-50 cursor-pointer">
                            <i className="ri-check-line text-xs" />
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); deleteNotif(n.id); }} className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-gray-200 text-red-400 hover:bg-red-50 cursor-pointer">
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {flatFiltered.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                      <i className="ri-notification-off-line text-xl text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-400">No notifications found</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: detail + preferences */}
          <div className="space-y-4">
            {selected ? (
              <DetailPanel
                notif={selected}
                onMarkRead={() => markRead(selected.id)}
                onDelete={() => deleteNotif(selected.id)}
                onClose={() => setSelected(null)}
              />
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 min-h-64 flex items-center justify-center">
                <div className="text-center px-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                    <i className="ri-notification-3-line text-2xl text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 font-medium">Select a notification</p>
                  <p className="text-xs text-gray-400 mt-1">Click any alert to view details and take action</p>
                </div>
              </div>
            )}

            {/* Alert Preferences */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-settings-3-line text-gray-500 text-sm" /></span>
                <h3 className="font-semibold text-sm text-gray-800">Alert Preferences</h3>
              </div>
              <div className="space-y-2">
                {[
                  { label: "Job Deadline Alerts", key: "deadline", enabled: true },
                  { label: "Overdue Invoice Alerts", key: "payment", enabled: true },
                  { label: "Approval Requests", key: "approval", enabled: true },
                  { label: "Deliverable Reviews", key: "deliverable", enabled: true },
                  { label: "Job Assignments", key: "assignment", enabled: true },
                  { label: "Status Changes", key: "status", enabled: true },
                  { label: "System Events", key: "system", enabled: false },
                ].map((pref) => (
                  <AlertToggle key={pref.key} label={pref.label} defaultEnabled={pref.enabled} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ERPLayout>
  );
}
